import {
  BriefcaseIcon,
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  InfoIcon,
  LinkIcon,
  ListIcon,
  MapPinIcon,
  PencilIcon,
  PlusIcon,
  Search,
  SheetIcon,
  SmileIcon,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AccreditationGroup, TicketAllocation } from "@/models/accreditation";
import { Menu } from "@headlessui/react";
import { EmojiPicker } from "@/crud/form/EmojiPicker";
import { Formik, FormikValues } from "formik";
import yup from "@/crud/yup-extended";
import { debounce, some } from "lodash";
import axios from "axios";
import { BASE_URL } from "@/dashboardQueries";
import { GroupOptionsButton } from "./GroupOptionsDropdown";
import { useAccreditationStore } from "@/store/accreditation";
import clsx from "clsx";

interface Props {
  group: AccreditationGroup;
  openCreateGroupDrawer: () => void;
  onGroupUpdate: () => void;
}

export const GroupDetails = ({
  group,
  openCreateGroupDrawer,
  onGroupUpdate,
}: Props) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingIcon, setIsEditingIcon] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const {
    setSearchOpen,
    setSelectedIndividualViewMode,
    selectedIndividualViewMode,
    setAllocateTicketsOpen,
  } = useAccreditationStore();

  const individualViewModes = [
    {
      name: "Table",
      icon: ListIcon,
      current: true,
    },
    {
      name: "Sheet",
      icon: SheetIcon,
    },
  ];

  const descriptionInputRef = useRef<HTMLInputElement>(null);
  // Focus on description when you edit it
  useEffect(() => {
    if (isEditingDescription && descriptionInputRef.current) {
      descriptionInputRef.current.focus();
    }
  }, [isEditingDescription]);

  const handleNameClick = () => {
    setIsEditingName(true);
  };

  const handleDescriptionClick = () => {
    setIsEditingDescription(true);
  };

  const handleIconClick = () => {
    setEmojiPickerOpen(true);
    setIsEditingIcon(true);
  };

  const debouncedSubmit = useCallback(
    debounce((handleSubmit) => {
      handleSubmit();
    }, 750),
    []
  );

  const updateGroupDetails = async (values: FormikValues) => {
    try {
      const response = await axios.put(
        `${BASE_URL}/accreditation/groups/${group.id}`,
        values
      );
      onGroupUpdate();
    } catch (error) {}
  };

  return (
    <div className="lg:flex lg:items-center lg:justify-between hover-parent cursor-text">
      <div className="min-w-0 flex-1">
        {false ? (
          <div className="animate-pulse">
            <div className="h-4 bg-gray-300 dark:bg-zinc-800 rounded w-1/4 mb-4"></div>
            <div className="h-8 bg-gray-300 dark:bg-zinc-800 rounded w-1/2 mb-2"></div>
          </div>
        ) : (
          <Formik
            initialValues={{
              name: group.name,
              description: group.description,
              icon: group.icon,
            }}
            validationSchema={yup.object({
              name: yup.string().required("Name is required"),
              description: yup.string().nullable(),
              icon: yup.string().nullable(),
            })}
            onSubmit={(values: FormikValues) => {
              updateGroupDetails(values);
            }}
            enableReinitialize
            validateOnBlur={false}
            validateOnChange={false}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
            }) => {
              useEffect(() => {
                debouncedSubmit(handleSubmit);
                return () => {
                  debouncedSubmit.cancel();
                };
              }, [values.name, values.description, values.icon]);

              return (
                <form>
                  <div className="ml-2">
                    <nav aria-label="Breadcrumb" className="flex">
                      <ol role="list" className="flex items-center space-x-4">
                        <li>
                          <div className="flex items-center">
                            <Link
                              to={`/dashboard/accreditation/groups`}
                              className="text-sm font-medium dark:text-gray-300 dark:hover:text-white text-gray-500 hover:text-gray-700 transition duration-200"
                            >
                              Groups
                            </Link>
                          </div>
                        </li>
                        {group?.hierarchy.map((item, index) => (
                          <li key={index}>
                            <div className="flex items-center">
                              <ChevronRightIcon
                                aria-hidden="true"
                                className="size-5 shrink-0 text-gray-500"
                              />
                              <Link
                                to={`/dashboard/accreditation/groups/${item.id}`}
                                className={`ml-4 text-sm font-medium dark:hover:text-white ${
                                  index === group.hierarchy.length - 1
                                    ? "text-gray-800 dark:text-white hover:text-gray-600 transition duration-200"
                                    : "dark:text-gray-300 text-gray-500"
                                }`}
                              >
                                {item.name}
                              </Link>
                            </div>
                          </li>
                        ))}
                      </ol>
                    </nav>
                  </div>
                  <div className="relative flex items-center mt-4">
                    <div className={values.icon ? "ml-1.5 mr-1 z-10" : "z-10"}>
                      {isEditingIcon ? (
                        <EmojiPicker
                          name="icon"
                          open={emojiPickerOpen}
                          onSelect={(emoji: any) => {
                            setFieldValue("icon", emoji);
                            handleSubmit();
                            setIsEditingIcon(false);
                          }}
                        />
                      ) : (
                        <span
                          className="text-2xl cursor-pointer"
                          onClick={handleIconClick}
                        >
                          {values.icon}
                        </span>
                      )}
                    </div>
                    {isEditingName ? (
                      <input
                        className="focus:ring-0 focus:ring-offset-0 p-0 pl-1.5 text-2xl/7 font-bold text-white sm:truncate sm:text-3xl sm:tracking-tight bg-transparent border-none focus:outline-none"
                        type="text"
                        name="name"
                        value={values.name}
                        onChange={(e) => {
                          handleChange(e);
                          debouncedSubmit(handleSubmit);
                        }}
                      />
                    ) : (
                      <h2
                        className="text-2xl/7 font-bold dark:text-white text-zinc-800 sm:truncate sm:text-3xl sm:tracking-tight cursor-pointer"
                        onClick={handleNameClick}
                      >
                        <span className="ml-1.5">{values.name}</span>
                      </h2>
                    )}

                    <div className="mr-2 mt-5 flex lg:ml-4 lg:mt-0 gap-x-2 flex-1 justify-end">
                      <button
                        type="button"
                        onClick={() => setSearchOpen(true)}
                        className="dark:bg-zinc-800 bg-gray-50 border flex items-center pr-1.5 pl-1.5 rounded-lg dark:border-white/10 dark:shadow-lg cursor-pointer dark:hover:bg-zinc-800/40 transition"
                      >
                        <div className="mr-2 ml-0.5">
                          <Search
                            size={20}
                            className="dark:text-zinc-500 text-zinc-600"
                          />
                        </div>
                        <div className="border px-2 py-0.5 rounded-lg dark:border-white/15">
                          <h4 className="text-sm -mt-0.5 text-zinc-500">/</h4>
                        </div>
                      </button>
                      <div
                        // onClick={() => setSearchOpen(true)}
                        className="dark:bg-zinc-800 bg-gray-50 border flex items-center pr-1 pl-1 py-0.5 rounded-lg dark:border-white/10 dark:shadow-lg cursor-pointer"
                      >
                        {individualViewModes.map((mode, index) => (
                          <button
                            key={mode.name}
                            type="button"
                            onClick={() =>
                              setSelectedIndividualViewMode(mode.name)
                            }
                            className={clsx(
                              "rounded-md p-1 border flex items-center relative has-tooltip",
                              index == 0 ? "mr-0.5" : "ml-0.5",
                              mode.name === selectedIndividualViewMode
                                ? "dark:bg-zinc-900 dark:border-white/15"
                                : "border-transparent dark:text-zinc-400 dark:hover:text-zinc-500"
                            )}
                          >
                            <mode.icon className="size-4 dark:text-zinc-500 text-zinc-600" />
                            <div className="z-50 tooltip rounded shadow-lg px-2 py-1 -mt-9 ml-3 top-0 -translate-x-1/2 text-nowrap bg-white dark:bg-dark-secondary text-xs text-gray-400 border dark:border-white/10 font-light">
                              {mode.name} view
                            </div>
                          </button>
                        ))}
                      </div>
                      <GroupOptionsButton isInGroup />
                    </div>
                  </div>
                  <div className="ml-1.5 mt-2">
                    {isEditingDescription ? (
                      <input
                        type="text"
                        name="description"
                        ref={descriptionInputRef}
                        className="w-full focus:ring-0 focus:ring-offset-0 p-0 dark:text-gray-400 text-zinc-700 sm:truncate bg-transparent border-none focus:outline-none"
                        value={values.description ? values.description : ""}
                        onChange={(e) => {
                          handleChange(e);
                          debouncedSubmit(handleSubmit);
                        }}
                        // onBlur={handleSubmit}
                      />
                    ) : (
                      group?.description && (
                        <div className="">
                          <p
                            className="dark:text-gray-400 text-zinc-700 cursor-pointer"
                            onClick={handleDescriptionClick}
                          >
                            {group?.description}
                          </p>
                        </div>
                      )
                    )}
                    {(!values.description || !values.icon) && (
                      <div className="text-white -ml-0.5">
                        <div className="flex">
                          <div className="flex text-zinc-400 text-sm">
                            {!values.icon && !isEditingIcon && (
                              <button
                                type="button"
                                onClick={() => {
                                  setIsEditingIcon(true);
                                  setEmojiPickerOpen(true);
                                }}
                                className="flex items-center mr-2 dark:hover:bg-zinc-800 py-1 px-1.5 rounded-md"
                              >
                                <SmileIcon className="h-4 w-4 mr-1.5" />
                                Add icon
                              </button>
                            )}
                            {!values.description && !isEditingDescription && (
                              <button
                                type="button"
                                onClick={() => {
                                  setIsEditingDescription(true);
                                  descriptionInputRef.current?.focus();
                                }}
                                className="flex items-center dark:hover:bg-zinc-800 py-1 px-1.5 rounded-md text-gray-500 dark:text-gray-300"
                              >
                                <InfoIcon className="h-4 w-4 mr-1.5" />
                                Add description
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="mr-2 -mb-1">
                      <div className="flex items-center mt-2">
                        <div
                          data-sidebar="group-label"
                          className="-mt-1 dark:text-gray-500 text-zinc-800 flex h-8 shrink-0 items-center rounded-md text-sm font-medium text-sidebar-foreground/70 outline-none ring-sidebar-ring transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&amp;>svg]:size-4 [&amp;>svg]:shrink-0 group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0"
                        >
                          Tickets
                        </div>
                        <div className="flex-1 justify-end flex pt-0.5">
                          <button
                            onClick={() => {
                              setAllocateTicketsOpen(true);
                            }}
                            type="button"
                            className="rounded border dark:border-white/5 dark:bg-white/10 px-1 py-1 text-sm font-semibold dark:text-white shadow-sm hover:bg-white/20"
                          >
                            <PlusIcon className="h-4 w-4 dark:text-white text-gray-500" />
                          </button>
                        </div>
                      </div>

                      <div className="-mt-1">
                        {group.groupAllocatedTickets.length > 0 ? (
                          <div>
                            {group.groupAllocatedTickets.map(
                              (ticketAllocation: TicketAllocation) => (
                                <span className="mr-1.5 mb-1 inline-flex items-center rounded-md bg-gray-400/10 px-2 py-1 text-xs font-medium text-gray-400 ring-1 ring-inset ring-gray-400/20">
                                  {ticketAllocation.claimed}/
                                  {ticketAllocation.quantity}{" "}
                                  {ticketAllocation.ticket.name}
                                </span>
                              )
                            )}
                          </div>
                        ) : (
                          <p className="dark:text-gray-400 text-zinc-800 text-xs">
                            There are no tickets associated with this group
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </form>
              );
            }}
          </Formik>
        )}
      </div>
    </div>
  );
};
